import React, { useMemo, useEffect } from "react";
import {
  FileSpreadsheet,
  Highlighter,
  ListTodo,
  Sparkles,
  BookOpen,
  Send,
  Star,
  MoreHorizontal,
  ListPlus,
} from "lucide-react";
import { motion } from "framer-motion";
import { ExpandableTabs } from "@/components/ui/expandable-tabs";
import { useI18n } from "@/lib/i18n";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useUserSettings } from "@/contexts/user-settings-context";

interface ContentTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  isImmersiveMode?: boolean;
  onShareClick: () => void;
  onStarClick: () => void;
  onAddToListClick: () => void;
  isStarred: boolean;
}

export function ContentTabs({
  activeTab,
  onTabChange,
  isImmersiveMode = false,
  onShareClick,
  onStarClick,
  onAddToListClick,
  isStarred,
}: ContentTabsProps) {
  const { t, lang } = useI18n() as any;
  const weightClass = lang === "chinese" ? "font-normal" : "font-medium";
  const isMobile = useIsMobile();
  const { userSettings } = useUserSettings();

  // Check if user's language setting is Chinese
  const isChineseLanguage = useMemo(() => {
    const langSetting = userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
    return langSetting === 'zh' || langSetting === 'chinese';
  }, [userSettings?.languageSetting]);

  const tabConfig = [
    // { key: 'summary', icon: FileText, value: 'Summary' },
    // { key: 'mindmap', icon: Network, value: 'Mindmap' },
    { key: 'transcript', icon: FileSpreadsheet, value: 'Transcript' },
    { key: 'vocabulary', icon: Highlighter, value: 'Vocabulary' },
    { key: 'phrase', icon: BookOpen, value: 'Phrase' },
    { key: 'quiz', icon: ListTodo, value: 'Quiz' },
  ] as const;

  // Filter out 'phrase' tab if user's language is not Chinese
  const filteredTabConfig = useMemo(() => {
    return tabConfig.filter(tab => {
      if (tab.key === 'phrase' && !isChineseLanguage) {
        return false;
      }
      return true;
    });
  }, [isChineseLanguage]);

  const tabData = filteredTabConfig.map(({ key, icon, value }) => {
    const name = t(`tabs.${key}.name`);
    const description = t(`tabs.${key}.desc`);
    const mobile = t(`tabs.${key}.mobile`, name);

    return {
      key,
      icon,
      value,
      name,
      description,
      mobile,
    };
  });

  // If user is on Phrase tab but language is not Chinese, switch to Transcript tab
  useEffect(() => {
    if (activeTab === 'Phrase' && !isChineseLanguage) {
      onTabChange('Transcript');
    }
  }, [activeTab, isChineseLanguage, onTabChange]);

  const activeIndex = tabData.findIndex(({ value }) => value === activeTab);

  const renderActionButtons = (buttonSizeClass = "h-9 w-9") => (
    <>
      {/* <Button
        variant="ghost"
        size="sm"
        onClick={onShareClick}
        className={cn(
          buttonSizeClass,
          "p-0 rounded-lg hover:bg-muted/80 group"
        )}
        title={t("episode.actions.share", "Share Episode")}
      >
        <Send className="h-4 w-4 group-hover:scale-110 transition-transform" />
      </Button> */}

      <Button
        variant="ghost"
        size="sm"
        onClick={onStarClick}
        className={cn(
          buttonSizeClass,
          "p-0 rounded-lg hover:bg-muted/80 group"
        )}
        title={
          isStarred
            ? t("episode.actions.removeFavorite", "Remove from favorites")
            : t("episode.actions.addFavorite", "Add to favorites")
        }
      >
        <Star
          className={`h-4 w-4 group-hover:scale-110 transition-all ${
            isStarred
              ? "fill-yellow-400 text-yellow-400"
              : "group-hover:text-yellow-500"
          }`}
        />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              buttonSizeClass,
              "p-0 rounded-lg hover:bg-muted/80 group"
            )}
            title={t("episode.actions.more", "More options")}
          >
            <MoreHorizontal className="h-4 w-4 group-hover:scale-110 transition-transform" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48 bg-background/95 backdrop-blur-sm border border-border/50 rounded-xl shadow-lg"
        >
          <DropdownMenuItem
            onClick={onAddToListClick}
            className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer rounded-lg hover:bg-muted/80"
          >
            <ListPlus className="h-4 w-4" />
            {t("episode.actions.addToPlaylist", "Add to Playlist")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onShareClick}
            className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer rounded-lg hover:bg-muted/80"
          >
            <Send className="h-4 w-4" />
            {t("episode.actions.share", "Share Episode")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{
        opacity: isImmersiveMode ? 0 : 1,
        y: 0,
        height: isImmersiveMode ? 0 : "auto",
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={`relative transition-all duration-300 overflow-hidden bg-background sm:bg-background/80 backdrop-blur-lg
        ${isImmersiveMode ? "hidden" : ""}`}
    >
      <div className="max-w-7xl mx-auto sm:px-6 px-0 md:px-2 py-0 sm:py-0">
        <div className="relative flex flex-col gap-3 sm:gap-0 sm:flex-row sm:items-center sm:justify-between">
          {isMobile ? (
            <ExpandableTabs
              tabs={tabData.map(({ mobile, icon, key }) => {
                switch (key) {
                  case "vocabulary":
                    return {
                      title: mobile,
                      imageSrc: "/images/vocabulary.png",
                      imageClassName: "transform scale-90",
                      selectedImageWrapperClassName: "-ml-[10px] -mt-[8px]",
                      selectedImageClassName:
                        "transform -translate-x-0.5 -translate-y-0.5 scale-[1.05] rotate-6",
                    };
                  case "transcript":
                    return {
                      title: mobile,
                      imageSrc: "/images/transcript.png",
                      imageClassName: "transform scale-90",
                      selectedImageWrapperClassName: "-ml-[10px] -mt-[8px]",
                      selectedImageClassName:
                        "transform -translate-x-0.5 -translate-y-0.5 scale-[1.05] rotate-6",
                    };
                  case "quiz":
                    return {
                      title: mobile,
                      imageSrc: "/images/test.png",
                      imageClassName: "transform scale-90",
                      selectedImageWrapperClassName: "-ml-[10px] -mt-[8px]",
                      selectedImageClassName:
                        "transform -translate-x-0.5 -translate-y-0.5 scale-[1.05] rotate-6",
                    };
                  case "phrase":
                    return {
                      title: mobile,
                      imageSrc: "/images/dictionary.png",
                      imageClassName: "transform scale-90",
                      selectedImageWrapperClassName: "-ml-[10px] -mt-[8px]",
                      selectedImageClassName:
                        "transform -translate-x-0.5 -translate-y-0.5 scale-[1.05] rotate-6",
                    };
                  default:
                    return {
                      title: mobile,
                      icon,
                    };
                }
              })}
              selectedIndex={activeIndex === -1 ? null : activeIndex}
              onChange={(index) => {
                if (index === null) return;
                const target = tabData[index];
                if (target) {
                  onTabChange(target.value);
                }
              }}
              className="w-full"
              tabsContainerClassName="flex-1"
              actions={
                <div className="flex items-center gap-2 pr-1 pl-2 border-l border-border/40">
                  {renderActionButtons("h-6 w-6 sm:h-9 sm:w-9")}
                </div>
              }
            />
          ) : (
            <nav
              className="flex overflow-x-auto scrollbar-hide py-0.5 sm:py-0 sm:pb-0 
              max-w-[calc(100vw-2rem)] sm:max-w-none overscroll-x-contain touch-pan-x"
            >
              <div className="flex items-center gap-2 sm:gap-4 min-w-max px-3 sm:px-0">
                {tabData.map(({ key, icon: Icon, value, name, description, mobile }, index) => {
                  const isActive = value === activeTab;
                  return (
                  <motion.button
                    key={key}
                    onClick={() => onTabChange(value)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.2 }}
                    className={`group font-light relative py-2 sm:py-3 px-5 sm:px-4 text-sm sm:text-base ${weightClass} 
                      transition-all duration-200 flex items-center gap-2 whitespace-nowrap
                      rounded-[12px] sm:rounded-none hover:scale-105 sm:hover:scale-100
                      ${isActive
                        ? "text-foreground bg-primary/15 sm:bg-transparent" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50 sm:hover:bg-transparent"
                      }`}
                    title={description}
                  >
                    {/* Icon with enhanced styling */}
                    <div className={`relative transition-all duration-200 ${
                      isActive ? "scale-110" : "group-hover:scale-110"
                    }`}>
                      <Icon className="h-[19px] w-[19px]" />
                      {isActive && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute -top-1 -right-1 w-2 h-2"
                        >
                          <Sparkles className="h-2 w-2 text-primary animate-pulse" />
                        </motion.div>
                      )}
                    </div>
                    
                    {/* Tab text */}
                    <span className="hidden sm:inline">{name}</span>
                    
                    {/* Mobile tab name - abbreviated */}
                    <span className="sm:hidden text-sm">{mobile}</span>

                    {/* Active indicator for desktop */}
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="hidden sm:block absolute bottom-0 left-0 right-0 h-[5px] bg-red-500 rounded-full"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}

                    {/* Hover indicator for desktop */}
                    <div className={`hidden sm:block absolute bottom-0 left-0 right-0 h-1 
                      transition-all duration-200 rounded-full
                      ${isActive ? "opacity-0" : "bg-muted-foreground/20 scale-x-0 group-hover:scale-x-100"}
                    `} />
                  </motion.button>
                );})}
              </div>
            </nav>
          )}

          {!isMobile && (
            <div className="hidden sm:flex items-center gap-2 sm:gap-3 pl-3 border-l border-border/40">
              {renderActionButtons()}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
